import {
  booleanAttribute,
  Component,
  ElementRef,
  effect,
  forwardRef,
  input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { clamp, noop, replace } from 'lodash-es';

const MIN_LENGTH = 4;
const MAX_LENGTH = 6;

function clampLength(value: number | string): number {
  return clamp(+value, MIN_LENGTH, MAX_LENGTH);
}

function normalizeValue(value: string, maxLength: number): string {
  return replace(value, /[^a-z0-9]/gi, '')
    .toUpperCase()
    .slice(0, maxLength);
}

@Component({
  selector: 'shared-code',
  imports: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CodeComponent),
      multi: true,
    },
  ],
  templateUrl: './code.component.html',
  styleUrl: './code.component.scss',
})
export class CodeComponent implements ControlValueAccessor, Validator {
  length = input(MAX_LENGTH, { transform: clampLength });
  required = input(false, { transform: booleanAttribute });

  @ViewChildren('codeInput') private codeInputs?: QueryList<ElementRef<HTMLInputElement>>;

  protected value = '';
  protected disabled = false;

  private onChange: (value: string) => void = noop;
  private onTouched: () => void = noop;
  private onValidatorChange: () => void = noop;

  constructor() {
    effect(() => {
      this.required();
      const normalizedValue = normalizeValue(this.value, this.length());

      if (normalizedValue !== this.value) {
        this.value = normalizedValue;
        this.onChange(this.value);
      }

      this.onValidatorChange();
    });
  }

  protected get indexes(): number[] {
    return Array.from({ length: this.length() }, (_, index) => index);
  }

  protected getCharacter(index: number): string {
    return this.value[index] ?? '';
  }

  writeValue(value: unknown): void {
    this.value = normalizeValue(value as string, this.length());
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    if (this.required() && !this.value) {
      return { required: true };
    }

    return null;
  }

  protected handleInput(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const characters = normalizeValue(inputElement.value, this.length());

    if (!characters) {
      this.setCharacter(index, '');
      inputElement.value = '';
      return;
    }

    this.setCharacters(index, characters);
  }

  protected handlePaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();

    const characters = normalizeValue(event.clipboardData?.getData('text') as string, this.length() - index);

    if (characters) {
      this.setCharacters(index, characters);
    }
  }

  protected handleKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.getCharacter(index) && index > 0) {
      this.focusInput(index - 1);
    }
  }

  protected markAsTouched(): void {
    this.onTouched();
  }

  private setCharacter(index: number, character: string): void {
    const characters = this.value.padEnd(this.length()).split('');
    characters[index] = character;
    this.commitValue(characters.join('').replace(/\s/g, ''), index);
  }

  private setCharacters(startIndex: number, charactersToSet: string): void {
    const characters = this.value.padEnd(this.length()).split('');

    for (let offset = 0; offset < charactersToSet.length && startIndex + offset < this.length(); offset++) {
      characters[startIndex + offset] = charactersToSet[offset];
    }

    this.commitValue(characters.join('').replace(/\s/g, ''), startIndex + charactersToSet.length);
  }

  private commitValue(nextValue: string, focusIndex: number): void {
    this.value = normalizeValue(nextValue, this.length());
    this.onChange(this.value);
    this.onValidatorChange();
    this.focusInput(Math.min(focusIndex, this.length() - 1));
  }

  private focusInput(index: number): void {
    queueMicrotask(() => {
      this.codeInputs!.get(index)!.nativeElement.focus();
      this.codeInputs!.get(index)!.nativeElement.select();
    });
  }
}
